import type {
  DashboardCategoryRow,
  DashboardGroupRow,
  DashboardSubcategoryRow,
  DashboardTransaction,
  TransactionDashboardData,
} from "../types";

export function formatMonthKey(ym: string): string {
  const [y, m] = ym.split("-").map((x) => Number.parseInt(x, 10));
  if (!y || !m) return ym;
  const d = new Date(y, m - 1, 1);
  const s = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
  return s.replace(/\.$/, "").replace(" de ", "/").replace(/\s+/g, "");
}

export function totalForMonth(
  totals: { month: string; totalAmount: number }[],
  month: string
): number {
  return totals.find((t) => t.month === month)?.totalAmount ?? 0;
}

/** YYYY-MM a partir de competenceDate (API / relatório por competência). */
export function competenceYearMonth(dateStr: string | null | undefined): string | null {
  const d = dateStr?.trim();
  if (!d || d.length < 7) return null;
  return d.slice(0, 7);
}

export function getCategoryTransactions(category: DashboardCategoryRow): DashboardTransaction[] {
  const subs = category.subcategories;
  if (subs && subs.length > 0) {
    return subs.flatMap((s) => s.transactions ?? []);
  }
  return category.transactions ?? [];
}

export function transactionsForMonthInDashboard(
  dashboard: TransactionDashboardData,
  monthKey: string
): DashboardTransaction[] {
  const out: DashboardTransaction[] = [];
  for (const g of dashboard.groups) {
    for (const c of g.categories) {
      for (const t of getCategoryTransactions(c)) {
        if (competenceYearMonth(t.competenceDate) === monthKey) out.push(t);
      }
    }
  }
  return out;
}

export function transactionsForGroupMonth(
  group: DashboardGroupRow,
  monthKey: string
): DashboardTransaction[] {
  const out: DashboardTransaction[] = [];
  for (const c of group.categories) {
    for (const t of getCategoryTransactions(c)) {
      if (competenceYearMonth(t.competenceDate) === monthKey) out.push(t);
    }
  }
  return out;
}

export function transactionsForCategoryMonth(
  category: DashboardCategoryRow,
  monthKey: string
): DashboardTransaction[] {
  return getCategoryTransactions(category).filter(
    (t) => competenceYearMonth(t.competenceDate) === monthKey
  );
}

export function transactionsForSubcategoryMonth(
  sub: DashboardSubcategoryRow,
  monthKey: string
): DashboardTransaction[] {
  return (sub.transactions ?? []).filter(
    (t) => competenceYearMonth(t.competenceDate) === monthKey
  );
}

function normPlan(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function isEntradasPlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  return n === "entradas" || n === "entrada";
}

function isImpostosPlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  return n === "impostos" || n === "imposto";
}

function isCustosVariaveisPlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  return n.includes("custos") && n.includes("variave");
}

function isDespesaFixaPlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  return n.includes("fixa") && (n.includes("despesa") || n.includes("despesas"));
}

function isOutrosPlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  return n === "outros" || n === "outro" || n === "outras despesas";
}

function isValoresPessoaisSeparatePlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  if (n.includes("movimentacoes") && n.includes("pessoais")) return true;
  if (n.includes("valores") && n.includes("pessoais")) return true;
  return false;
}

function isInvestimentosFinanciamentosSeparatePlan(planGroup: string): boolean {
  const n = normPlan(planGroup);
  if (n.includes("investimentos")) return true;
  if (n.includes("emprestimos") && n.includes("financiamentos")) return true;
  if (n.includes("inv/emp") || (n.includes("inv") && n.includes("financ"))) return true;
  return false;
}

function sumGroupsForMonth(groups: DashboardGroupRow[], monthKey: string): number {
  return groups.reduce((acc, g) => acc + totalForMonth(g.monthlyTotals, monthKey), 0);
}

export type DerivedDashboardLayout = {
  preMarginRows: DashboardGroupRow[];
  otherAfterMargin: DashboardGroupRow[];
  fixedExpenseGroups: DashboardGroupRow[];
  valoresPessoaisPlans: DashboardGroupRow[];
  investimentosFinanciamentosPlans: DashboardGroupRow[];
  marginMonthly: number[];
  lucroMonthly: number[];
  valoresPessoaisMonthly: number[];
  investimentosFinanciamentosMonthly: number[];
  geracaoReservaMonthly: number[];
  entradasMonthly: number[];
};

export function computeDerivedDashboard(data: TransactionDashboardData): DerivedDashboardLayout {
  const entradas: DashboardGroupRow[] = [];
  const impostos: DashboardGroupRow[] = [];
  const custosVar: DashboardGroupRow[] = [];
  const fixed: DashboardGroupRow[] = [];
  const outros: DashboardGroupRow[] = [];
  const otherMisc: DashboardGroupRow[] = [];
  const valoresPessoaisPlans: DashboardGroupRow[] = [];
  const investimentosFinanciamentosPlans: DashboardGroupRow[] = [];

  for (const g of data.groups) {
    if (isValoresPessoaisSeparatePlan(g.planGroup)) {
      valoresPessoaisPlans.push(g);
      continue;
    }
    if (isInvestimentosFinanciamentosSeparatePlan(g.planGroup)) {
      investimentosFinanciamentosPlans.push(g);
      continue;
    }
    if (isEntradasPlan(g.planGroup)) entradas.push(g);
    else if (isImpostosPlan(g.planGroup)) impostos.push(g);
    else if (isCustosVariaveisPlan(g.planGroup)) custosVar.push(g);
    else if (isDespesaFixaPlan(g.planGroup)) fixed.push(g);
    else if (isOutrosPlan(g.planGroup)) outros.push(g);
    else otherMisc.push(g);
  }

  const preMarginRows = [...entradas, ...impostos, ...custosVar];
  const otherAfterMargin = [...outros, ...otherMisc];
  const keys = data.monthKeys;

  const entradasMonthly = keys.map((mk) => sumGroupsForMonth(entradas, mk));
  const impostosMonthly = keys.map((mk) => sumGroupsForMonth(impostos, mk));
  const custosMonthly = keys.map((mk) => sumGroupsForMonth(custosVar, mk));
  const marginMonthly = keys.map(
    (_, i) => entradasMonthly[i] - Math.abs(impostosMonthly[i]) - Math.abs(custosMonthly[i])
  );
  const outrosMonthly = keys.map((mk) => sumGroupsForMonth(outros, mk));
  const fixedSumMonthly = keys.map((mk) => sumGroupsForMonth(fixed, mk));
  const lucroMonthly = keys.map(
    (_, i) => marginMonthly[i] - Math.abs(outrosMonthly[i]) - Math.abs(fixedSumMonthly[i])
  );

  const valoresPessoaisMonthly = keys.map((mk) => sumGroupsForMonth(valoresPessoaisPlans, mk));
  const investimentosFinanciamentosMonthly = keys.map((mk) =>
    sumGroupsForMonth(investimentosFinanciamentosPlans, mk)
  );
  const geracaoReservaMonthly = keys.map(
    (_, i) =>
      lucroMonthly[i] - valoresPessoaisMonthly[i] - investimentosFinanciamentosMonthly[i]
  );

  return {
    preMarginRows,
    otherAfterMargin,
    fixedExpenseGroups: fixed,
    valoresPessoaisPlans,
    investimentosFinanciamentosPlans,
    marginMonthly,
    lucroMonthly,
    valoresPessoaisMonthly,
    investimentosFinanciamentosMonthly,
    geracaoReservaMonthly,
    entradasMonthly,
  };
}
